/**
 * Centralized AG Grid styling configurations.
 * Separates CSS variables (style prop) from MUI overrides (sx prop).
 * Supports three table modes: CompSheet, Financial, and Index.
 */

// ============================================
// Table Mode Constants
// ============================================

/**
 * Table mode identifiers for different grid configurations
 */
export const TABLE_MODES = {
  COMPSHEET: 'compsheet',
  FINANCIAL: 'financial',
  INDEX: 'index',
  SIMPLE: 'simple', // For key-value displays like Metrics.jsx
};

/**
 * Mode-specific height configurations
 */
export const MODE_HEIGHTS = {
  [TABLE_MODES.COMPSHEET]: {
    rowHeight: 24,
    headerHeight: 55,
    pinnedRowHeight: 28,
  },
  [TABLE_MODES.FINANCIAL]: {
    rowHeight: 22, // Denser rows for IB model
    calculationRowHeight: 18, // Very compact calculation rows
    sectionHeaderHeight: 28, // Section headers slightly taller
    headerHeight: 35,
  },
  [TABLE_MODES.INDEX]: {
    rowHeight: 24,
    headerHeight: 35,
  },
  [TABLE_MODES.SIMPLE]: {
    rowHeight: 28, // Slightly taller for readability
    headerHeight: 32,
  },
};

/**
 * Get mode-specific heights
 * @param {string} mode - Table mode (compsheet, financial, index)
 * @returns {Object} Height configuration for the mode
 */
export const getModeHeights = (mode) => MODE_HEIGHTS[mode] || MODE_HEIGHTS[TABLE_MODES.COMPSHEET];

// ============================================
// CSS Variables for AG Grid (use in style prop)
// ============================================

/**
 * Get AG Grid CSS variables for theming
 * @param {Object} theme - MUI theme object
 * @param {string} mode - Optional table mode for mode-specific overrides
 * @returns {Object} CSS variables object for style prop
 */
export const getAgGridCssVars = (theme, mode) => {
  const heights = getModeHeights(mode);

  return {
    '--ag-background-color': theme.table.header,
    '--ag-header-background-color': theme.table.agGridHeader,
    '--ag-header-foreground-color': theme.table.agGridHeaderText,
    '--ag-odd-row-background-color': theme.tableHeader.highlight,
    '--ag-row-hover-color': theme.tableHeader.highlightDark,
    '--ag-material-primary-color': theme.palette.primary.main,
    '--ag-material-accent-color': theme.palette.primary.main,
    '--ag-header-height': `${heights.headerHeight}px`,
    '--ag-font-family': theme.typography.fontFamily,
    '--ag-font-size': '12px',
    '--ag-row-height': `${heights.rowHeight}px`,
    '--ag-row-border-color': 'transparent',
  };
};

/**
 * Get AG Grid CSS variables with custom overrides
 * @param {Object} theme - MUI theme object
 * @param {Object} overrides - Custom CSS variable overrides
 * @param {string} mode - Optional table mode
 * @returns {Object} Merged CSS variables object
 */
export const getAgGridCssVarsWithOverrides = (theme, overrides = {}, mode) => ({
  ...getAgGridCssVars(theme, mode),
  ...overrides,
});

// ============================================
// MUI sx Overrides for AG Grid (use in sx prop)
// ============================================

/**
 * Get AG Grid MUI sx overrides for consistent header styling
 * @param {Object} theme - MUI theme object
 * @param {string} mode - Optional table mode for mode-specific styles
 * @returns {Object} sx prop object
 */
export const getAgGridSxOverrides = (theme, mode) => {
  const baseStyles = {
    '& .ag-root-wrapper': {
      backgroundColor: theme.table.header,
      fontSize: '12px',
    },
    '& .ag-root, & .ag-body-viewport, & .ag-center-cols-viewport, & .ag-center-cols-container': {
      backgroundColor: theme.table.header,
    },
    '& .ag-body-vertical-scroll, & .ag-body-vertical-scroll-viewport, & .ag-body-horizontal-scroll, & .ag-body-horizontal-scroll-viewport, & .ag-horizontal-left-spacer, & .ag-horizontal-right-spacer': {
      backgroundColor: theme.table.header,
    },
    '& .ag-header': {
      backgroundColor: theme.table.agGridHeader,
      color: theme.table.agGridHeaderText,
      fontSize: '12px',
    },
    '& .ag-header-cell': {
      backgroundColor: theme.table.agGridHeader,
      color: theme.table.agGridHeaderText,
      letterSpacing: 'normal',
      wordSpacing: 'normal',
      whiteSpace: 'normal',
      lineHeight: '1.2em',
      textAlign: 'center',
      padding: '0px 8px',
      fontSize: '12px',
      borderRight: theme.palette.mode === 'light'
        ? '1px solid rgba(0, 0, 0, 0.06)'
        : '1px solid rgba(255, 255, 255, 0.04)',
    },
    '& .ag-header-cell:last-child': {
      borderRight: 'none',
    },
    '& .ag-header-cell-label': {
      justifyContent: 'center',
    },
    '& .ag-header-group-cell': {
      backgroundColor: theme.table.agGridHeader,
      color: theme.table.agGridHeaderText,
      letterSpacing: 'normal',
      wordSpacing: 'normal',
      whiteSpace: 'normal',
      lineHeight: '1.2em',
      justifyContent: 'center',
      textAlign: 'center',
    },
    '& .ag-header-group-cell .ag-header-cell-comp-wrapper, & .ag-header-group-cell .ag-cell-label-container': {
      justifyContent: 'center',
      textAlign: 'center',
      width: '100%',
    },
    '& .ag-header-group-cell-label': {
      justifyContent: 'center',
      textAlign: 'center',
      width: '100%',
    },
    '& .ag-header-group-text': {
      display: 'block',
      overflow: 'hidden',
      textAlign: 'center',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    },
    '& .ag-header-cell-text': {
      letterSpacing: 'normal',
      textTransform: 'none',
      wordSpacing: 'normal',
      whiteSpace: 'normal',
      fontSize: '12px',
    },
    '& .ag-cell': {
      fontSize: '12px',
      padding: '0px 6px',
      borderRight: theme.palette.mode === 'light'
        ? '1px solid rgba(0, 0, 0, 0.06)'
        : '1px solid rgba(255, 255, 255, 0.04)',
    },
    '& .ag-cell:last-child': {
      borderRight: 'none',
    },
  };

  // Mode-specific style overrides
  const modeStyles = {
    [TABLE_MODES.FINANCIAL]: {
      '& .right-align-header .ag-header-cell-label': {
        justifyContent: 'flex-end',
        textAlign: 'right',
        overflow: 'visible',
      },
      '& .left-align-header .ag-header-cell-label': {
        justifyContent: 'flex-start',
        textAlign: 'left',
        overflow: 'visible',
      },
    },
    [TABLE_MODES.INDEX]: {
      '& .ag-cell': {
        fontSize: '12px',
        padding: '0px 4px',
        borderRight: theme.palette.mode === 'light'
          ? '1px solid rgba(0, 0, 0, 0.06)'
          : '1px solid rgba(255, 255, 255, 0.04)',
      },
      '& .ag-cell:last-child': {
        borderRight: 'none',
      },
    },
    [TABLE_MODES.SIMPLE]: {
      '& .ag-header': {
        display: 'none', // Hide header for simple key-value displays
      },
      '& .ag-cell': {
        fontSize: '13px',
        padding: '4px 8px',
        borderRight: 'none',
      },
      '& .ag-cell:first-of-type': {
        fontWeight: 500,
        color: theme.palette.text.secondary,
      },
    },
  };

  return {
    ...baseStyles,
    ...(modeStyles[mode] || {}),
  };
};

/**
 * Get AG Grid sx overrides with custom additions
 * @param {Object} theme - MUI theme object
 * @param {Object} customSx - Additional sx properties
 * @param {string} mode - Optional table mode
 * @returns {Object} Merged sx object
 */
export const getAgGridSxWithCustom = (theme, customSx = {}, mode) => ({
  ...getAgGridSxOverrides(theme, mode),
  ...customSx,
});

// ============================================
// Row Style Functions
// ============================================

/**
 * Get row style function for alternating row colors
 * @param {Object} theme - MUI theme object
 * @param {string} mode - Optional table mode
 * @returns {Function} AG Grid getRowStyle function
 */
export const getAlternatingRowStyle = (theme, mode) => (params) => {
  if (params.node.rowPinned === 'bottom') {
    return {
      backgroundColor: theme.table.header,
      color: theme.palette.text.secondary,
      fontWeight: 'bold',
      textAlign: 'right',
    };
  }
  return params.node.rowIndex % 2 === 0
    ? { backgroundColor: theme.tableHeader.highlight, textAlign: 'right' }
    : { backgroundColor: theme.table.header, textAlign: 'right' };
};

/**
 * Get row style for pinned rows only (summary rows)
 * @param {Object} theme - MUI theme object
 * @returns {Object} Style object for pinned rows
 */
export const getPinnedRowStyle = (theme) => ({
  backgroundColor: theme.table.header,
  color: theme.palette.text.secondary,
  fontWeight: 'bold',
  textAlign: 'center',
});

/**
 * Get row style function for financial mode (value/calculation rows)
 * Alternates every row: value rows are dark, calculation rows are light
 * @param {Object} theme - MUI theme object
 * @returns {Function} AG Grid getRowStyle function
 */
export const getFinancialRowStyle = (theme) => (params) => {
  const isCalculationRow = params.data?.type === 'calculation';
  const isGroupHeader = params.data?.isGroupHeader;

  if (isGroupHeader) {
    return {
      backgroundColor: theme.table.header,
      fontWeight: 'bold',
    };
  }

  // Value rows (first row of each pair) are light, calculation rows are dark
  if (isCalculationRow) {
    return {
      fontStyle: 'italic',
      color: theme.palette.text.secondary,
      backgroundColor: theme.tableHeader.highlight, // Dark background for calculation rows
      display: 'flex',
      alignItems: 'center',
    };
  }

  return {
    backgroundColor: theme.table.header, // Light background for value rows
  };
};

/**
 * Get row height function for financial mode
 * @param {Object} heights - Height configuration from getModeHeights
 * @returns {Function} AG Grid getRowHeight function
 */
export const getFinancialRowHeight = (heights) => (params) => {
  if (params.data?.isSectionHeader) {
    return heights.sectionHeaderHeight || 28;
  }
  return params.data?.type === 'calculation'
    ? heights.calculationRowHeight
    : heights.rowHeight;
};

/**
 * Get row style function for IB financial mode with section headers, subtotals,
 * and industry-standard underlines.
 *
 * Reads `metricIndex` directly from row data (pre-computed during row construction)
 * for O(1) banding instead of O(n) loop computation.
 *
 * Supports `underlineType`: 'single' (subtotals) or 'double' (grand totals).
 * Supports `sectionIndex` on section headers for top-border separation.
 *
 * @param {Object} theme - MUI theme object
 * @returns {Function} AG Grid getRowStyle function
 */
export const getIBFinancialRowStyle = (theme) => (params) => {
  const data = params.data;

  // Section header rows
  if (data?.isSectionHeader) {
    return {
      backgroundColor: theme.tableHeader?.sectionHeader || theme.table.header,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '10px',
      letterSpacing: '0.5px',
      borderBottom: `2px solid ${theme.palette.primary.main}`,
      ...(data.sectionIndex > 0 && {
        borderTop: `1px solid ${theme.palette.divider}`,
      }),
    };
  }

  // Read pre-computed metricIndex from row data
  const metricIndex = data?.metricIndex ?? 0;
  const bg = metricIndex % 2 === 0 ? theme.table.header : theme.tableHeader.highlight;

  // Underline styling (applied to last row of a metric group)
  const underlineStyle = {};
  if (data?.underlineType === 'single') {
    underlineStyle.borderBottom = `1px solid ${theme.palette.text.primary}`;
  } else if (data?.underlineType === 'double') {
    underlineStyle.borderBottom = `3px double ${theme.palette.text.primary}`;
  }

  // Calculation rows inherit their parent metric's background
  if (data?.type === 'calculation') {
    return {
      fontStyle: 'italic',
      color: theme.palette.text.secondary,
      backgroundColor: bg,
      fontSize: '10px',
      ...underlineStyle,
    };
  }

  // Subtotal rows (key metrics like Revenue, GM, OpInc, NI, FCF)
  if (data?.isSubtotal) {
    return {
      fontWeight: 600,
      backgroundColor: bg,
      ...underlineStyle,
    };
  }

  // Regular value rows
  return { backgroundColor: bg, ...underlineStyle };
};

// ============================================
// Default Column Definitions
// ============================================

/**
 * Standard default column definition
 */
export const DEFAULT_COL_DEF = {
  filter: true,
  floatingFilter: false,
  sortable: true,
  resizable: true,
  suppressMenuHide: false,
  suppressHeaderMenuButton: false,
};

/**
 * Default column definition for read-only grids
 */
export const DEFAULT_COL_DEF_READONLY = {
  ...DEFAULT_COL_DEF,
  editable: false,
  suppressMovable: true,
};

/**
 * Mode-specific default column definitions
 */
export const MODE_DEFAULT_COL_DEFS = {
  [TABLE_MODES.COMPSHEET]: {
    ...DEFAULT_COL_DEF,
    sortable: true,
    filter: true,
    resizable: true,
  },
  [TABLE_MODES.FINANCIAL]: {
    sortable: false,
    filter: false,
    resizable: false,
    suppressMovable: true,
    editable: false,
  },
  [TABLE_MODES.INDEX]: {
    sortable: true,
    filter: false,
    resizable: false,
    suppressMovable: true,
    lockPinned: true,
    lockVisible: true,
  },
  [TABLE_MODES.SIMPLE]: {
    sortable: false,
    filter: false,
    resizable: false,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
  },
};

/**
 * Get mode-specific default column definition
 * @param {string} mode - Table mode
 * @returns {Object} Default column definition for the mode
 */
export const getModeDefaultColDef = (mode) => MODE_DEFAULT_COL_DEFS[mode] || DEFAULT_COL_DEF;

// ============================================
// Grid Theme Class Helper
// ============================================

/**
 * Get AG Grid theme class based on MUI theme mode
 * @param {Object} theme - MUI theme object
 * @returns {string} AG Grid theme class name
 */
export const getAgGridThemeClass = (theme) =>
  theme.palette.mode === 'dark' ? 'ag-theme-material-dark' : 'ag-theme-material';
